// src/services/aiProgressService.ts
import { StaffAllocationGrid } from '../types/grid';
import { AIAssignmentSuggestion } from './openaiService';

export interface ChunkProgress {
  gridId: string;
  totalChunks: number;
  completedChunks: number;
  processedClasses: Set<string>;
  suggestions: AIAssignmentSuggestion[];
  startTime: number;
  lastUpdateTime: number;
  status: 'in_progress' | 'completed' | 'failed' | 'paused';
  error?: string;
  sessionId: string;
}

export interface ChunkMetadata {
  chunkNumber: number;
  classIds: string[];
  processed: boolean;
  retryCount: number;
  error?: string;
}

class AIProgressService {
  private readonly STORAGE_KEY = 'ai_chunk_progress';
  private readonly SESSION_TIMEOUT = 60 * 60 * 1000; // 1 hour
  private readonly MAX_RETRIES = 3;
  private currentProgress: ChunkProgress | null = null;
  private chunkMetadata: Map<number, ChunkMetadata> = new Map();
  private abortController: AbortController | null = null;

  /**
   * Initialize or resume progress for a grid
   */
  initializeProgress(gridId: string, totalChunks: number): ChunkProgress {
    // Check for existing progress
    const existingProgress = this.loadProgress(gridId);
    
    if (existingProgress && this.isProgressValid(existingProgress)) {
      // Resume existing progress
      this.currentProgress = existingProgress;
      this.loadChunkMetadata();
      return existingProgress;
    }

    // Create new progress
    const newProgress: ChunkProgress = {
      gridId,
      totalChunks,
      completedChunks: 0,
      processedClasses: new Set(),
      suggestions: [],
      startTime: Date.now(),
      lastUpdateTime: Date.now(),
      status: 'in_progress',
      sessionId: this.generateSessionId()
    };

    this.currentProgress = newProgress;
    this.saveProgress();
    return newProgress;
  }

  /**
   * Check if existing progress is still valid
   */
  private isProgressValid(progress: ChunkProgress): boolean {
    const timeSinceUpdate = Date.now() - progress.lastUpdateTime;
    return (
      progress.status === 'in_progress' &&
      timeSinceUpdate < this.SESSION_TIMEOUT
    );
  }

  /**
   * Update progress after processing a chunk
   */
  updateChunkProgress(
    chunkNumber: number,
    classIds: string[],
    suggestions: AIAssignmentSuggestion[],
    success: boolean = true,
    error?: string
  ): void {
    if (!this.currentProgress) return;

    // Update chunk metadata
    const metadata = this.chunkMetadata.get(chunkNumber) || {
      chunkNumber,
      classIds,
      processed: false,
      retryCount: 0
    };

    if (success) {
      metadata.processed = true;
      metadata.error = undefined;
      
      // Add processed classes
      classIds.forEach(id => this.currentProgress!.processedClasses.add(id));
      
      // Add suggestions
      this.currentProgress.suggestions.push(...suggestions);
      this.currentProgress.completedChunks++;
    } else {
      metadata.retryCount++;
      metadata.error = error;
    }

    this.chunkMetadata.set(chunkNumber, metadata);
    this.currentProgress.lastUpdateTime = Date.now();
    
    // Check if all chunks are complete
    if (this.currentProgress.completedChunks >= this.currentProgress.totalChunks) {
      this.currentProgress.status = 'completed';
    }

    this.saveProgress();
    this.saveChunkMetadata();
  }

  /**
   * Get chunks that need processing (new or failed with retries remaining)
   */
  getChunksToProcess(allChunkNumbers: number[]): number[] {
    if (!this.currentProgress) return allChunkNumbers;

    return allChunkNumbers.filter(chunkNum => {
      const metadata = this.chunkMetadata.get(chunkNum);
      
      if (!metadata) return true; // New chunk
      if (metadata.processed) return false; // Already done
      if (metadata.retryCount >= this.MAX_RETRIES) return false; // Too many retries
      
      return true; // Can retry
    });
  }

  /**
   * Mark a chunk for retry
   */
  markChunkForRetry(chunkNumber: number, error: string): void {
    const metadata = this.chunkMetadata.get(chunkNumber);
    if (metadata) {
      metadata.error = error;
      metadata.retryCount++;
      this.chunkMetadata.set(chunkNumber, metadata);
      this.saveChunkMetadata();
    }
  }

  /**
   * Pause current processing
   */
  pauseProcessing(): void {
    if (this.currentProgress) {
      this.currentProgress.status = 'paused';
      this.saveProgress();
    }
    
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  /**
   * Resume processing
   */
  resumeProcessing(): void {
    if (this.currentProgress && this.currentProgress.status === 'paused') {
      this.currentProgress.status = 'in_progress';
      this.currentProgress.lastUpdateTime = Date.now();
      this.saveProgress();
    }
  }

  /**
   * Clear progress for a grid
   */
  clearProgress(gridId: string): void {
    try {
      const allProgress = this.loadAllProgress();
      delete allProgress[gridId];
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(allProgress));
      
      // Clear chunk metadata
      localStorage.removeItem(`${this.STORAGE_KEY}_chunks_${gridId}`);
      
      if (this.currentProgress?.gridId === gridId) {
        this.currentProgress = null;
        this.chunkMetadata.clear();
      }
    } catch (error) {
      console.error('Error clearing progress:', error);
    }
  }

  /**
   * Get current progress
   */
  getCurrentProgress(): ChunkProgress | null {
    return this.currentProgress;
  }

  /**
   * Get progress for a specific grid
   */
  getProgress(gridId: string): ChunkProgress | null {
    return this.loadProgress(gridId);
  }

  /**
   * Check if there's resumable progress for a grid
   */
  hasResumableProgress(gridId: string): boolean {
    const progress = this.loadProgress(gridId);
    return !!(
      progress &&
      progress.status === 'in_progress' &&
      progress.completedChunks < progress.totalChunks &&
      this.isProgressValid(progress)
    );
  }

  /**
   * Get progress percentage
   */
  getProgressPercentage(): number {
    if (!this.currentProgress) return 0;
    if (this.currentProgress.totalChunks === 0) return 0;
    return Math.round(
      (this.currentProgress.completedChunks / this.currentProgress.totalChunks) * 100
    );
  }

  /**
   * Get estimated time remaining
   */
  getEstimatedTimeRemaining(): number | null {
    if (!this.currentProgress) return null;
    if (this.currentProgress.completedChunks === 0) return null;

    const elapsed = Date.now() - this.currentProgress.startTime;
    const avgTimePerChunk = elapsed / this.currentProgress.completedChunks;
    const remainingChunks = this.currentProgress.totalChunks - this.currentProgress.completedChunks;
    
    return Math.round(avgTimePerChunk * remainingChunks);
  }

  /**
   * Save progress to localStorage
   */
  private saveProgress(): void {
    if (!this.currentProgress) return;

    try {
      const allProgress = this.loadAllProgress();
      
      // Convert Set to Array for serialization
      const progressToSave = {
        ...this.currentProgress,
        processedClasses: Array.from(this.currentProgress.processedClasses)
      };
      
      allProgress[this.currentProgress.gridId] = progressToSave;
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(allProgress));
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  }

  /**
   * Save chunk metadata
   */
  private saveChunkMetadata(): void {
    if (!this.currentProgress) return;

    try {
      const metadataArray = Array.from(this.chunkMetadata.entries());
      localStorage.setItem(
        `${this.STORAGE_KEY}_chunks_${this.currentProgress.gridId}`,
        JSON.stringify(metadataArray)
      );
    } catch (error) {
      console.error('Error saving chunk metadata:', error);
    }
  }

  /**
   * Load chunk metadata
   */
  private loadChunkMetadata(): void {
    if (!this.currentProgress) return;

    try {
      const stored = localStorage.getItem(
        `${this.STORAGE_KEY}_chunks_${this.currentProgress.gridId}`
      );
      
      if (stored) {
        const metadataArray = JSON.parse(stored);
        this.chunkMetadata = new Map(metadataArray);
      }
    } catch (error) {
      console.error('Error loading chunk metadata:', error);
      this.chunkMetadata = new Map();
    }
  }

  /**
   * Load progress from localStorage
   */
  private loadProgress(gridId: string): ChunkProgress | null {
    try {
      const allProgress = this.loadAllProgress();
      const progress = allProgress[gridId];
      
      if (progress) {
        // Convert Array back to Set
        return {
          ...progress,
          processedClasses: new Set(progress.processedClasses as any)
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error loading progress:', error);
      return null;
    }
  }

  /**
   * Load all progress data
   */
  private loadAllProgress(): Record<string, any> {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Error loading all progress:', error);
      return {};
    }
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Clean up old/expired progress data
   */
  cleanupExpiredProgress(): void {
    try {
      const allProgress = this.loadAllProgress();
      const now = Date.now();
      let hasChanges = false;

      Object.keys(allProgress).forEach(gridId => {
        const progress = allProgress[gridId];
        const timeSinceUpdate = now - progress.lastUpdateTime;
        
        // Remove if expired or completed more than 24 hours ago
        if (
          timeSinceUpdate > this.SESSION_TIMEOUT ||
          (progress.status === 'completed' && timeSinceUpdate > 24 * 60 * 60 * 1000)
        ) {
          delete allProgress[gridId];
          localStorage.removeItem(`${this.STORAGE_KEY}_chunks_${gridId}`);
          hasChanges = true;
        }
      });

      if (hasChanges) {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(allProgress));
      }
    } catch (error) {
      console.error('Error cleaning up expired progress:', error);
    }
  }

  /**
   * Export progress data for debugging
   */
  exportProgressData(): any {
    return {
      currentProgress: this.currentProgress,
      chunkMetadata: Array.from(this.chunkMetadata.entries()),
      allProgress: this.loadAllProgress()
    };
  }
}

// Export singleton instance
export const aiProgressService = new AIProgressService();